import { Component, inject } from '@angular/core';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { FrontendFieldDefinition } from 'src/modules/sm-base/models/frontend-field-definition.model';
import { FrontendFieldListItem } from 'src/modules/sm-base/models/frontend-field-list-item.model';
import { FrontendFieldType } from 'src/modules/sm-base/models/frontend-field-type.enum';
import { FrontendFormDefinition } from 'src/modules/sm-base/models/frontend-form-definition.model';
import { TableCellType } from 'src/modules/sm-base/models/table-cell-type.enum';
import { TableColumn } from 'src/modules/sm-base/models/table-column.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { TableSortColumn } from 'src/modules/sm-base/models/table-sort-column.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { EnaioCertificateService } from '../../services/enaio-certificate.service';
import { ClassFieldTrip } from '../../shared/class-field-trip.entity';
import { ClassRegister } from '../../shared/class-register.entity';
import { EnaioCertificatesTools } from '../../shared/enaio-certificates-tools';
import { ObjectOfSimpleDataTypes } from 'src/modules/utils/shared/object-of-simple-data-types.model';


@Component({
    selector: 'app-class-field-trips',
    templateUrl: './class-field-trips.component.html',
    styleUrls: ['./class-field-trips.component.scss']
  })
  export class ClassFieldTripsComponent extends ComponentView {

    service = inject(EnaioCertificateService);

    neededParams: ObjectOfSimpleDataTypes = { id: "number" };
    id: number;

    classRegister: ClassRegister;
    orgData: ClassFieldTrip[];
    table: TableData;
    isManager = false;

    async initParams(): Promise<boolean> {
        this.classRegister = await this.service.restGetClassRegister(this.id, ["fieldTrips"], null, true);
        let settings = await this.service.restGetClassRegisterSettings(this.classRegister.enaioSchoolClass.getYearNumber(), this.classRegister.isHalfYear);
        this.isManager = this.classRegister.enaioSchoolClass.isManager(this.app.getUserName(), settings);

        this.orgData = Utils.cloneDeep(this.classRegister.fieldTrips);

        this.table = new TableData([
            new TableColumn("fromDate", "Von", TableCellType.date, { autoWidth: 1 }),
            new TableColumn("toDate", "Bis", TableCellType.date, { autoWidth: 1 }),
            new TableColumn("fromHour", "Von Stunde", TableCellType.text, { autoWidth: 10 }),
            new TableColumn("toHour", "Bis Stunde", TableCellType.text, { autoWidth: 10 }),
            new TableColumn("title", "Bezeichnung", TableCellType.text, { flex: 1 }),
            new TableColumn("approved", "Freigabe", TableCellType.text, { autoWidth: 10 }),
            new TableColumn("edit", "", TableCellType.button, { autoWidth: 2 })
        ], this.classRegister.fieldTrips.map(h => new TableRow("", h,
              {
                ... h,
                fromHour: h.fromHour == -1 ? "Ganzer Tag" : h.fromHour + 1,
                toHour: h.toHour == -1 ? "Ganzer Tag" : h.toHour + 1,
                approved: h.approvedDate != null ? h.approvedBy + " " + Utils.dateFormatDefaultDate(h.approvedDate) : this.isManager ? "NEIN (Haken klicken für Freigabe)" : "NEIN",
                edit: (this.isManager && h.approvedDate == null ? "fas fa-check," : "") + "fas fa-pencil,fas fa-trash"
            }
        )), [new TableSortColumn("from")]);

        await this.service.updateNavigationClassRegister(this.id, [
            { label: "Unterrichtsgänge", routerLink: ["/enaio-certificates", "class-registers", "field-trips", this.id] }
        ]);
        return true;
    }

    override getCanDeactivateMessage(): string {
        return this.isChanged() ? "" : null;
    }

    isChanged(): boolean {
        return !Utils.equalsDeep(this.fromGui(), this.orgData);
    }

    fromGui(): ClassFieldTrip[] {
        return this.table.rows.map(h => h.raw as ClassFieldTrip);
    }

    async save(): Promise<void> {
        await this.app.saveDataHandler(async () => RestEndpoint.main().query({ id: this.id }).body(this.fromGui()).post().run("api/cer/classregister/fieldTrips").getText(), this, true, true);
    }

    getForm(): FrontendFormDefinition {
        return new FrontendFormDefinition([
            new FrontendFieldDefinition("fromDate", "Von", FrontendFieldType.datePicker, { mandatory: true, value: new Date(), guiGroup: "fromDate", guiWidthInGroup: 50 }),
            new FrontendFieldDefinition("fromHour", "Stunde", FrontendFieldType.comboBox, {mandatory: true, value: -1, guiGroup: "fromDate", guiWidthInGroup: 50, dropdownEditable: false, minValue: -1, listItems: [new FrontendFieldListItem(-1, "Ganzer Tag"), ...Utils.getRange(0, EnaioCertificatesTools.getMaxHoursPerDay() - 1).map(hour => new FrontendFieldListItem(hour, "ab " + (hour + 1) + ". Stunde"))]}),
            new FrontendFieldDefinition("toDate", "Bis", FrontendFieldType.datePicker, {mandatory: true, value: new Date(), guiGroup: "toDate", guiWidthInGroup: 50}),
            new FrontendFieldDefinition("toHour", "Stunde", FrontendFieldType.comboBox, {mandatory: true, value: -1, guiGroup: "toDate", guiWidthInGroup: 50, dropdownEditable: false, minValue: -1, listItems: [new FrontendFieldListItem(-1, "Ganzer Tag"), ...Utils.getRange(0, EnaioCertificatesTools.getMaxHoursPerDay() - 1).map(hour => new FrontendFieldListItem(hour, "bis " + (hour + 1) + ". Stunde"))]}),
            new FrontendFieldDefinition("title", "Bezeichnung", FrontendFieldType.text, { mandatory: true }),
            new FrontendFieldDefinition("type", "Art des Unterrichtsgangs", FrontendFieldType.comboBox, { listItems: ["", "Wanderung", "Exkursion", "Theater-/Konzert-/Kinobesuch", "Museumsbesuch"].map(s => new FrontendFieldListItem(s, s)) }),
            new FrontendFieldDefinition("educationalProject", "Pädagogisches Vorhaben", FrontendFieldType.textArea),
            new FrontendFieldDefinition("transportation", "Transportmittel", FrontendFieldType.comboBox, { listItems: ["", "ÖPNV", "Dienstfahrzeuge"].map(s => new FrontendFieldListItem(s, s)) }),
            new FrontendFieldDefinition("inClassPupilsSupervision", "Betreuung der in der Klasse verbleibenden Schüler", FrontendFieldType.textArea)
        ]);
    }

    async addFieldTrip(): Promise<void> {
        let fieldTrip = new ClassFieldTrip();
        let form = this.getForm();
        form.fill(fieldTrip);
        if (await this.app.messageDialog.form(form, "Unterrichtsgang anlegen") == "ok") {
            form.get(fieldTrip);
            let [ok, _] = await this.app.saveDataHandler(async () => RestEndpoint.main().post().query({id: this.classRegister.id}).body(fieldTrip).run("api/cer/classregister/fieldTrip").getText(), this, true, false);
            if (ok) {
                await this.initParams();
            }
        }
    }

    async fieldTripClicked(event: any): Promise<void> {
        if (event[2] as string == "fas fa-pencil") {
            let fieldTrip = (event[0] as TableRow).raw as ClassFieldTrip;
            let form = this.getForm();
            form.fill(fieldTrip);
            if (await this.app.messageDialog.form(form, "Unterrichtsgang bearbeiten") == "ok") {
                form.get(fieldTrip);
                let [ok, _] = await this.app.saveDataHandler(async () => RestEndpoint.main().put().query({id: this.classRegister.id}).body(fieldTrip).run("api/cer/classregister/fieldTrip").getText(), this, true, false);
                if (ok) {
                    await this.initParams();
                }
            }
        }
        else if (event[2] as string == "fas fa-trash" && await this.app.messageDialog.yesNo("Wirklich löschen?", "Warnung") && (await this.app.deleteDataHandler(async () => RestEndpoint.main().delete().query({id: (event[0].raw as ClassFieldTrip).id}).run("api/cer/classregister/fieldTrip").getText()))[0]) {
            await this.initParams();
        }
        else if (event[2] as string == "fas fa-check" && await this.app.messageDialog.yesNo("Möchten Sie diesen Unterrichtsgang freigeben?", "Frage")) {
            let fieldTrip = (event[0] as TableRow).raw as ClassFieldTrip;
            let [ok, _] = await this.app.saveDataHandler(async () => RestEndpoint.main().post().query({id: fieldTrip.id}).body(fieldTrip).run("api/cer/classregister/fieldTripApprove").getText(), this, true, false);
            if (ok) {
                await this.initParams();
            }
        }
    }
}
